import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import IconCalendarDate from '../icons/IconCalendarDate';

const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };
const ISO_FORMAT = 'yyyy-MM-dd';

// Drop-in replacement for a plain <input type="date"> - same value contract
// (a 'yyyy-MM-dd' string in, the same string out via onChange, '' for
// unset) so every existing caller's state wiring works unchanged. Renders
// its own calendar grid instead of the browser's native picker chrome.
//
// The popup is portaled to document.body and positioned via the trigger's
// live getBoundingClientRect (same technique as TimezoneSelect.jsx) - this
// component gets used inside several field wrappers that set
// backdrop-filter (which traps a plain position:absolute child behind
// later siblings regardless of z-index), and inside narrow/short widgets
// (FilterBar's toolbar row, the lesson "hero" pills) that would otherwise
// clip a calendar-sized popup.
export default function DateInput({ value, onChange, placeholder, disabled, className, style, clearable = true, align = 'left', icon = true }) {
   const [open, setOpen] = useState(false);
   const [viewMonth, setViewMonth] = useState(() => (value ? DateTime.fromISO(value) : DateTime.now()).startOf('month'));
   const [menuRect, setMenuRect] = useState(null);
   const rootRef = useRef(null);
   const menuRef = useRef(null);

   const lang = language[language.lang];
   const locale = LUXON_LOCALE_BY_LANG[language.lang] || 'en';

   const selectedDt = useMemo(() => (value ? DateTime.fromISO(value) : null), [value]);
   const todayIso = useMemo(() => DateTime.now().toFormat(ISO_FORMAT), []);

   const displayValue = selectedDt && selectedDt.isValid ? selectedDt.setLocale(locale).toFormat('dd.MM.yyyy') : '';

   const updateMenuRect = () => {
      if (!rootRef.current) return;
      const r = rootRef.current.getBoundingClientRect();
      setMenuRect(align === 'right'
         ? { top: r.bottom + 4, right: window.innerWidth - r.right }
         : { top: r.bottom + 4, left: r.left });
   };

   useLayoutEffect(() => {
      if (!open) return;
      setViewMonth((value ? DateTime.fromISO(value) : DateTime.now()).startOf('month'));
      updateMenuRect();
      const onReposition = () => updateMenuRect();
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
         window.removeEventListener('scroll', onReposition, true);
         window.removeEventListener('resize', onReposition);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open]);

   useEffect(() => {
      const onClickOutside = (e) => {
         const inRoot = rootRef.current && rootRef.current.contains(e.target);
         const inMenu = menuRef.current && menuRef.current.contains(e.target);
         if (!inRoot && !inMenu) setOpen(false);
      };
      const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onKeyDown);
      return () => {
         document.removeEventListener('mousedown', onClickOutside);
         document.removeEventListener('keydown', onKeyDown);
      };
   }, []);

   const toggleOpen = () => { if (!disabled) setOpen(prev => !prev); };

   const choose = (iso) => {
      onChange(iso);
      setOpen(false);
   };

   const clear = (e) => {
      e.stopPropagation();
      onChange('');
   };

   // 6x7 grid starting on the Monday of the week containing the 1st, same
   // Monday-first convention weekDaysShort/weekday>=6 already use elsewhere
   // in this app (Events.jsx/GroupLessonsList.jsx weekdayInfo helpers).
   const weeks = useMemo(() => {
      const firstOfMonth = viewMonth;
      const gridStart = firstOfMonth.minus({ days: firstOfMonth.weekday - 1 });
      const days = [];
      for (let i = 0; i < 42; i++) days.push(gridStart.plus({ days: i }));
      const rows = [];
      for (let i = 0; i < 6; i++) rows.push(days.slice(i * 7, i * 7 + 7));
      return rows;
   }, [viewMonth]);

   const menu = open && menuRect ? createPortal(
      <div className='date-input-dropdown' ref={menuRef} style={{ position: 'fixed', ...menuRect }}>
         <div className='date-input-cal-header'>
            <span className='date-input-cal-nav' onClick={() => setViewMonth(m => m.minus({ months: 1 }))}>‹</span>
            <span className='date-input-cal-month'>{viewMonth.setLocale(locale).toFormat('LLLL yyyy')}</span>
            <span className='date-input-cal-nav' onClick={() => setViewMonth(m => m.plus({ months: 1 }))}>›</span>
         </div>
         <div className='date-input-cal-weekdays'>
            {lang.weekDaysShort.map((d, i) => <span key={i}>{d}</span>)}
         </div>
         <div className='date-input-cal-grid'>
            {weeks.map((week, wi) => (
               <div className='date-input-cal-week' key={wi}>
                  {week.map(day => {
                     const iso = day.toFormat(ISO_FORMAT);
                     const outside = day.month !== viewMonth.month;
                     const isSelected = selectedDt && iso === selectedDt.toFormat(ISO_FORMAT);
                     const isToday = iso === todayIso;
                     return (
                        <span key={iso}
                           className={'date-input-cal-day'
                              + (outside ? ' outside' : '')
                              + (isSelected ? ' selected' : '')
                              + (isToday ? ' today' : '')}
                           onClick={() => choose(iso)}>
                           {day.day}
                        </span>
                     );
                  })}
               </div>
            ))}
         </div>
         <div className='date-input-cal-footer'>
            <span className='date-input-cal-today-btn' onClick={() => choose(todayIso)}>{lang.today}</span>
         </div>
      </div>,
      document.body
   ) : null;

   return (
      <div className={'date-input' + (className ? ' ' + className : '')} style={style} ref={rootRef}>
         {icon ? <span className='date-input-icon'><IconCalendarDate /></span> : ''}
         <input type='text' className='date-input-field' readOnly disabled={disabled}
            placeholder={placeholder} value={displayValue}
            onClick={toggleOpen} />
         {clearable && value && !disabled ? (
            <span className='date-input-clear' onClick={clear}>×</span>
         ) : ''}
         {menu}
      </div>
   );
}
